import { useContext, useEffect, useState } from "react";
import { QueryClientContext } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as IconHome } from "../../PublicPage/Navi/Navi/icon/navi1.svg";
import { ReactComponent as IconSchedule } from "../../PublicPage/Navi/Navi/icon/navi2.svg";
import { ReactComponent as IconProduct } from "../../PublicPage/Navi/Navi/icon/navi4.svg";
import { ReactComponent as IconTheme } from "../../PublicPage/Navi/Navi/icon/navi5.svg";
import { ReactComponent as IconMypage } from "../../PublicPage/Navi/Navi/icon/navi6.svg";
import useAlarmNewCountREST from "../../PublicPage/Header/hooks/useAlarmNewCountREST.hook";
import useMainREST from "../../CustomerPage/MainHome/components/WrokRequiredNotification/hooks/useMainREST.hook";
import {
  NAVIGATION_ACTION_CODES,
  normalizeAppManifest,
} from "../../Common/appConfig/appConfig";
import { useAppManifest } from "../../Common/hooks/useAppConfig";
import { getCurrentCustomerId } from "../customerScheduleStorage";
import {
  CUSTOMER_ACTION_COUNT_EVENT,
  getCachedCustomerActionCount,
} from "../HomePage/customerActionItems";
import { isDemoMemberPreviewEnabled } from "../../Common/fetch/demoMember";
import { withMobileView } from "./mobileNavigation";
import "./MobileAppShell.css";

const MOBILE_HEADER_ASSETS = {
  bell: "/static/img/mobile-header/header-bell.svg",
  logoMaskA: "/static/img/mobile-header/header-logo-mask-a.svg",
  logoFillA: "/static/img/mobile-header/header-logo-fill-a.svg",
  logoMaskB: "/static/img/mobile-header/header-logo-mask-b.svg",
  logoFillB: "/static/img/mobile-header/header-logo-fill-b.svg",
  logoVectorA: "/static/img/mobile-header/header-logo-vector-a.svg",
  logoVectorB: "/static/img/mobile-header/header-logo-vector-b.svg",
  logoText: "/static/img/mobile-header/header-logo-text.svg",
};
const TAB_PRESENTATION = {
  [NAVIGATION_ACTION_CODES.home]: { id: "home", Icon: IconHome, activePaths: ["/", "/tabs/home"] },
  [NAVIGATION_ACTION_CODES.schedules]: { id: "schedule", Icon: IconSchedule, activePaths: ["/tabs/schedules"] },
  [NAVIGATION_ACTION_CODES.products]: { id: "product", Icon: IconProduct, activePaths: ["/tabs/products", "/productList"] },
  [NAVIGATION_ACTION_CODES.themes]: { id: "theme", Icon: IconTheme, activePaths: ["/tabs/themes", "/themeBook"] },
  [NAVIGATION_ACTION_CODES.mypage]: { id: "mypage", Icon: IconMypage, activePaths: ["/tabs/mypage"] },
};

const TAB_ICONS = {
  home: IconHome,
  schedule: IconSchedule,
  schedules: IconSchedule,
  product: IconProduct,
  products: IconProduct,
  theme: IconTheme,
  themes: IconTheme,
  mypage: IconMypage,
};

export function MobileTopHeader({ onBellClick, className = "", notificationCount = 0 }) {
  const navigate = useNavigate();
  const legacyApiEnabled = !isDemoMemberPreviewEnabled();
  const { alarmNewCountData = 0 } = useAlarmNewCountREST(legacyApiEnabled);
  const { mainData } = useMainREST(legacyApiEnabled);
  const customerId = getCurrentCustomerId();
  const [cachedActionCount, setCachedActionCount] = useState(() => getCachedCustomerActionCount({ customerId }));
  const serverActionCount = Math.max(0, Number(mainData?.thema_select_count) || 0) + Math.max(0, Number(mainData?.select_photo_count) || 0);
  const actionCount = Math.max(0, Number(notificationCount) || 0, cachedActionCount, serverActionCount);
  const visibleNotificationCount = actionCount + Math.max(0, Number(alarmNewCountData) || 0);

  useEffect(() => {
    setCachedActionCount(getCachedCustomerActionCount({ customerId }));

    const updateActionCount = (event) => {
      if (Number(event?.detail?.customerId) !== Number(customerId)) return;
      setCachedActionCount(Math.max(0, Number(event.detail.count) || 0));
    };

    window.addEventListener(CUSTOMER_ACTION_COUNT_EVENT, updateActionCount);
    return () => window.removeEventListener(CUSTOMER_ACTION_COUNT_EVENT, updateActionCount);
  }, [customerId]);

  const handleBellClick = () => {
    if (typeof onBellClick === "function") {
      onBellClick();
      return;
    }

    navigate(withMobileView("/tabs/home?screen=notifications"));
  };

  return (
    <header className={["mobile-top-header", className].filter(Boolean).join(" ")} data-node-id="7159:9351">
      <span className="mobile-top-header__logo" aria-label="My studio">
        <span className="mobile-top-header__mark" aria-hidden="true">
          <span
            className="mobile-top-header__logo-part-a"
            style={{ "--logo-mask": `url(${MOBILE_HEADER_ASSETS.logoMaskA})` }}
          >
            <img alt="" src={MOBILE_HEADER_ASSETS.logoFillA} />
          </span>
          <span
            className="mobile-top-header__logo-part-b"
            style={{ "--logo-mask": `url(${MOBILE_HEADER_ASSETS.logoMaskB})` }}
          >
            <img alt="" src={MOBILE_HEADER_ASSETS.logoFillB} />
          </span>
          <span className="mobile-top-header__logo-dot">
            <img alt="" src={MOBILE_HEADER_ASSETS.logoVectorA} />
          </span>
          <span className="mobile-top-header__logo-heart">
            <img alt="" src={MOBILE_HEADER_ASSETS.logoVectorB} />
          </span>
        </span>
        <img className="mobile-top-header__text" alt="My studio" src={MOBILE_HEADER_ASSETS.logoText} />
      </span>
      <button
        className="mobile-top-header__bell"
        type="button"
        aria-label={visibleNotificationCount > 0 ? `확인할 알림 ${visibleNotificationCount}개` : "알림"}
        onClick={handleBellClick}
      >
        <img alt="" src={MOBILE_HEADER_ASSETS.bell} />
        {visibleNotificationCount > 0 && (
          <span className="mobile-top-header__bell-count" aria-hidden="true">
            {visibleNotificationCount > 99 ? "99+" : visibleNotificationCount}
          </span>
        )}
      </button>
    </header>
  );
}
function MobileBottomTabBarView({ activePath, onNavigate, manifest }) {
  const location = useLocation();
  const routerNavigate = useNavigate();
  const pathname = activePath || location.pathname;
  const navigate = onNavigate || routerNavigate;
  const tabs = manifest.navigation.map((item) => {
    const presentation = TAB_PRESENTATION[item.actionCode];
    if (!presentation) return null;
    return {
      ...presentation,
      label: item.label,
      path: item.path,
      Icon: TAB_ICONS[item.iconKey] || presentation.Icon,
    };
  }).filter(Boolean);

  return (
    <nav
      className="mobile-bottom-tab-bar"
      aria-label="하단 탭"
      data-node-id="7424:4605"
    >
      {tabs.map((tab) => {
        const isActive = tab.activePaths.some((path) => pathname === path || (path !== "/" && pathname.startsWith(`${path}/`)));
        const TabIcon = tab.Icon;

        return (
          <button
            key={tab.path}
            type="button"
            className={isActive ? "mobile-bottom-tab-bar__item mobile-bottom-tab-bar__item--active" : "mobile-bottom-tab-bar__item"}
            data-tab-id={tab.id}
            aria-current={isActive ? "page" : undefined}
            onClick={() => navigate(withMobileView(tab.path))}
          >
            <span className="mobile-bottom-tab-bar__icon" aria-hidden="true">
              <TabIcon />
            </span>
            <span>{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

function ConfiguredMobileBottomTabBar(props) {
  const { manifest } = useAppManifest();
  return <MobileBottomTabBarView {...props} manifest={manifest} />;
}

export function MobileBottomTabBar(props) {
  const queryClient = useContext(QueryClientContext);

  if (!queryClient) {
    return <MobileBottomTabBarView {...props} manifest={normalizeAppManifest()} />;
  }

  return <ConfiguredMobileBottomTabBar {...props} />;
}
